import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { ICatalog, IGeneralRules, SaveCustomData } from '../../models/catalogs.models';
import { ILocation } from 'src/app/features/customers/models/customers.models';
import { IProduct } from 'src/app/features/produits/models/products.models';
import { IBrand } from 'src/app/features/brands/models/brands.models';
import { ICategory } from 'src/app/features/categories/models/categories.models';
import { ITag } from 'src/app/features/tags/models/tags.models';
import { AddCatalog, AddCatalogSuccess, UpdateCatalogSuccess } from '../../store/catalogs.actions';
import { Subscription, tap } from 'rxjs';
import { MatStepper } from '@angular/material/stepper';
import { EditBasicCatalogsInfosComponent } from '../edit-basic-catalogs-infos/edit-basic-catalogs-infos.component';
import { MatDialog } from '@angular/material/dialog';
import { EditGeneralRulesComponent } from '../edit-general-rules/edit-general-rules.component';
import { customValidator } from 'src/app/shared/validators/fields-validators';
import { CommonService } from 'src/app/shared/common.service';
import { TranslateService } from '@ngx-translate/core';
import { step1Schema, step3Schema } from '../../validators/catalogue.zod.validator';
import { ZodValidatorService } from 'src/app/shared/zod-validator/zod-validator.service';
import { ShareCatalogComponent } from '../share-catalog/share-catalog.component';
import { ConfirmNotificationComponent } from '../confirm-notification/confirm-notification.component';

@Component({
  selector: 'app-add-catalogs',
  templateUrl: './add-catalogs.component.html',
  styleUrls: ['./add-catalogs.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
})
export class AddCatalogsComponent implements OnInit {
  @ViewChild('stepper') stepper: MatStepper | undefined;
  form!: FormGroup;
  generalRulesform!: FormGroup;
  currentForm: string = "basicForm";
  currentSummary: string = "Summary";
  selectedProducts: IProduct[] = [];
  selectedLocations: ILocation[] = [];
  catalog: ICatalog | null = null;
  disableDate!: boolean;
  imageCatalog!: string;
  catalogImgFile!: File | undefined;
  defaultVariantAvatar: string = '../../../../../assets/images/uoload-image.jpeg'
  generalRules!: IGeneralRules;
  currentCustomDiscount!: number
  private subscriptions: Subscription[] = [];
  step1Completed = false;
  step2Completed = false;
  step3Completed = false;
  step4Completed = false;
  step5Completed = false;
  currentStep = 1;

  constructor(
    private fb: FormBuilder,
    private store: Store<AppState>,
    private dialog: MatDialog,
    private actions$: Actions,
    private router: Router,
    private translateService: TranslateService,
    private zodValidatorService: ZodValidatorService
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.initializeGeneralRulesForm();
    this.onToggleChange();
    const sub2 = this.actions$.pipe(
      ofType(AddCatalogSuccess, UpdateCatalogSuccess),
      tap((action) => {
        const catalog: ICatalog | undefined = action.catalog
        if(catalog && catalog.status === 'Approved') {
          const dialogRef = this.dialog.open(ShareCatalogComponent, {
            width: '600px',
            disableClose: false,
            data: {
              catalogId: catalog.id,
              catalogName: catalog.name
            }
          });

          dialogRef.afterClosed().subscribe(result => {
            if (result && result.success) {
              const notificationDialogRef = this.dialog.open(ConfirmNotificationComponent, {
                width: '600px',
                data: {
                  notifiedCount: result.notifiedCount
                }
              });

              notificationDialogRef.afterClosed().subscribe(result => {
                this.router.navigate(['catalogs'])
              })
            } else {
              this.router.navigate(['catalogs'])
            }
          });
        } else {
          this.router.navigate(['catalogs'])
        }
      })).subscribe();

      this.subscriptions.push(sub2)
  }
  markStepCompleted(step: number): void {
    switch (step) {
      case 1:
        this.step1Completed = true;
        break;
      case 2:
        this.step2Completed = true;
        break;
      case 3:
        this.step3Completed = true;
        break;
      case 4:
        this.step4Completed = true;
        break;
      case 5:
        this.step5Completed = true;
        break;
    }
  }
  onStepChange(event: any): void {
    const stepIndex = event.selectedIndex;
    this.markStepCompleted(stepIndex + 1);
  }
  completeStep(step: number): void {
    this.markStepCompleted(step);
  }

  private initializeGeneralRulesForm(generalRules?: IGeneralRules): void {
    this.generalRulesform = this.fb.group({
      general_discount: new FormControl(generalRules?.general_discount ?? 0, [Validators.required]),
      increment: new FormControl(generalRules?.increment ?? 1, [Validators.required]),
      max_qte: new FormControl(generalRules?.max_qte ?? null),
      min_qte: new FormControl(generalRules?.min_qte ?? 1, [Validators.required]),
      can_be_sold_out_of_stock: new FormControl(generalRules?.can_be_sold_out_of_stock ?? false, [Validators.required]),
      show_stock: new FormControl(generalRules?.show_stock ?? true, [Validators.required]),
    }, { validators: customValidator(this.translateService) });

    this.generalRulesform.get('show_stock')?.valueChanges.subscribe((value: boolean) => {
      if (value === false) this.generalRulesform.get('can_be_sold_out_of_stock')?.setValue(true);
    })

    this.generalRulesform.get('can_be_sold_out_of_stock')?.valueChanges.subscribe((value: boolean) => {
      if (value === false) this.generalRulesform.get('show_stock')?.setValue(true);
    })
  }

  initializeForm(): void {
    this.form = this.fb.group({
      name: new FormControl(this.catalog?.name ?? '', Validators.required),
      start_date: new FormControl(this.catalog?.start_date ?? new Date().toISOString().substring(0, 10), [this.validateStartDate.bind(this), Validators.required]),
      end_date: new FormControl(this.catalog?.end_date ?? '', [this.validateEndDate.bind(this)]),
      status: new FormControl(this.catalog?.status ?? 'Draft', Validators.required),
    });
  }

  sanitize(event: any) {
    event.target.value = event.target.value.replace(/[^\d]/g, '');
  }

  validateStartDate(control: any) {
    if (!control.value) {
      return null;
    }

    const today = new Date();
    const startDate = new Date(control.value);

    today.setHours(0, 0, 0, 0);
    startDate.setHours(0, 0, 0, 0);

    if (this.catalog && this.catalog.start_date) {
      const existingStartDate = new Date(this.catalog.start_date);
      existingStartDate.setHours(0, 0, 0, 0);

      if (startDate.getTime() === existingStartDate.getTime()) {

        return null;
      }
    }

    return startDate >= today ? null : { startDateInvalid: true };
  }

  validateEndDate(control: any) {
    if (!this.form) {
      return null;
    }
    if (!control.value) {
      return null;
    }
    const startDate = new Date(this.form.get('start_date')?.value);
    const endDate = new Date(control.value);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    return endDate >= startDate ? null : { endDateInvalid: true };
  }

  next(currentForm?: string): boolean | void{
    if(currentForm && currentForm === 'basic_infos' && (this.form.invalid || this.generalRulesform.invalid)){
      this.form.markAllAsTouched();
      this.generalRulesform.markAllAsTouched();
      return false
    }
    this.generalRules = this.generalRulesform.value
    const generalDiscount = this.generalRulesform.get('general_discount')?.value
    this.currentCustomDiscount = generalDiscount ? generalDiscount : undefined
    if(this.isStepValid()) {
      this.stepper?.next();
    }
  }

  isStepValid() {
    this.currentStep = this.stepper ? this.stepper?.selectedIndex + 1 : 0; // If stepper is undefined set currentStep to an unkown step so the stepValidation get bypassed

    return this.validateStep(this.currentStep)
  }

  validateStep(step: number) {
    let stepSchema : any = null;
    let stepData : any = null;

    if(step === 1) {
      stepData = {
        ...this.form.getRawValue(),
        ...this.generalRulesform.getRawValue(),
        image: this.catalogImgFile
      }

      stepSchema = step1Schema
    }

    else if (step === 3) {

      stepData = {
        products: this.selectedProducts,
      }

      stepSchema = step3Schema
    }

    if(stepSchema && stepData) {
      console.log(`Validating step ${step} data: `, stepData)
      const result = this.zodValidatorService.validate(stepSchema, stepData, {returnErrors: false, translationKey: 'catalogue.step' + step })
      console.log('Validation result', result)
      return result.success
    }

    return true;
  }

  back(){
    this.currentStep = this.stepper ? this.stepper?.selectedIndex + 1 : 0;
    this.stepper?.previous();
  }

  getCatalogImg(event: any) {
    let file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const dataUrl = e.target.result
        this.imageCatalog = dataUrl.split(',')[1]
        this.catalogImgFile = file
      };
      reader.readAsDataURL(file);
    }
  }
  cancelImage(){
    this.imageCatalog = ''
    this.catalogImgFile = undefined;
  }

  onToggleChange(){
    const endDateControle = this.form.get('end_date');
    if(!this.disableDate){
      endDateControle?.reset()
      endDateControle?.disable()
    } else {
      endDateControle?.enable()
    }
  }

  goTo(form: string): boolean | void{
    if(form === 'customProducts' && this.selectedProducts.length <= 0){
      return false
    }
    if(form === 'insertProducts' && !this.form.valid){
      this.form.markAllAsTouched();
      return false
    }
    this.currentSummary = form;
    const generalDiscount = this.generalRulesform.get('general_discount')?.value
    this.currentCustomDiscount = generalDiscount ? generalDiscount : undefined
  }

  removeLocation(location: ILocation): void | boolean {
    this.selectedLocations = this.selectedLocations.filter(l => l.id !== location.id);
  }

  onSaveProducts(data: {selectedProducts: IProduct[], missingPricing: boolean}){
    this.currentSummary = 'summary';
    this.next()
    this.selectedProducts = data.selectedProducts;
  }

  onSaveLocations(selectedLocations: ILocation[]){
    this.selectedLocations = selectedLocations;
    this.currentSummary = 'summary';
    this.next()
  }

  sumDiscount(a: number): number {
    const A = +a / 100;
    const B = this.generalRulesform.get('general_discount')?.value ? +this.generalRulesform.get('general_discount')?.value / 100 : 0;
    return Math.ceil(
      parseFloat(
        ((A + B - (A * B)) * 100).toFixed(1)
      )
    )
  }

  onSaveCustomProducts(customProducts: SaveCustomData){
    this.currentSummary = 'summary';
    this.selectedProducts = customProducts.customProducts;
    this.next()
  }

  saveGeneraleRule(generalRules: IGeneralRules){
    this.initializeGeneralRulesForm(generalRules)
    this.generalRules = generalRules
    const generalDiscount = this.generalRulesform.get('general_discount')?.value
    this.currentCustomDiscount = generalDiscount ? generalDiscount : undefined
  }

  cancel(submitType: string) {
    if(submitType === 'stepper'){
      this.back()
    }else if (submitType === 'summary'){
      this.currentSummary = 'summary';
    }
    this.currentForm = 'basicForm';

  }

  getUniqueBrands(): IBrand[] {
    const brandMap = new Map<number, IBrand>();
    this.selectedProducts.forEach(product => {
      if (product.brand && !brandMap.has(product.brand.id)) {
        if(product.brand.id) {
          brandMap.set(product.brand.id, product.brand);
        }
      }
    });

    return Array.from(brandMap.values());
  }
  getUniqueCategories(): ICategory[] {
    const categoryMap = new Map<number, ICategory>();

    this.selectedProducts.forEach(product => {
      product.categories?.forEach(category => {
        categoryMap.set(category.id, category);
      })
    });

    return Array.from(categoryMap.values());
  }
  getUniqueTags(): ITag[] {
    const tagMap = new Map<number, ITag>();
    this.selectedProducts.forEach(product => {
      if (product.tags) {
        product.tags.forEach(tag => {
          if (!tagMap.has(tag.id)) {
            tagMap.set(tag.id, tag);
          }
        });
      }
    });
    return Array.from(tagMap.values());
  }

  onGeneralDiscountChange(event: any){
      const input = event.target, key = event.data;
      if ([',', '.'].includes(key)) {
        return;
      }
      const value = input.value < 0 ? 0 : input.value > 100 ? 100 : +input.value;
      input.value = value
      this.generalRulesform.get('general_discount')?.setValue(value);
      this.selectedProducts = this.selectedProducts.map((product: IProduct, index) => {
        if(product.variants && product.variants.length > 0 ){
          this.selectedProducts[index].variants = this.selectedProducts[index].variants?.map(variant => {
            return {
              ...variant,
              discount: value,
            }
          })
        }
        return {
          ...product,
          discount: value,
        }
      })
    this.generalRules = this.generalRulesform.value
  }

  onGeneralIncrementChange(event: any){
    this.selectedProducts = this.selectedProducts.map((product: IProduct, index) => {
      if(product.variants && product.variants.length > 0 ){
        this.selectedProducts[index].variants = this.selectedProducts[index].variants?.map(variant => {
          return {
            ...variant,
            increment: event.target.value ? +event.target.value : undefined,
          }
        })
      }
      return {
        ...product,
        increment: event.target.value ? +event.target.value : undefined,
      }
    })
    this.generalRules = this.generalRulesform.value
  }

  onGeneralMinChange(event: any){
    const value = event.target.value;
    this.selectedProducts = this.selectedProducts.map((product: IProduct, index) => {
      if(product.variants && product.variants.length > 0 ){
        this.selectedProducts[index].variants = this.selectedProducts[index].variants?.map(variant => {
          return {
            ...variant,
            min_qte: value ? +value : undefined,
          }
        })
      }
      return {
        ...product,
        min_qte: value ? +value : undefined,
      }
    })
    this.generalRules = this.generalRulesform.value
  }

  onGeneralMaxChange(event: any){
    const value = event.target.value;
    this.selectedProducts = this.selectedProducts.map((product: IProduct, index) => {
      if(product.variants && product.variants.length > 0 ){
        this.selectedProducts[index].variants = this.selectedProducts[index].variants?.map(variant => {
          return {
            ...variant,
            max_qte: value ? +value : undefined,
          }
        })
      }
      return {
        ...product,
        max_qte: value ? +value : undefined,
      }
    })
    this.generalRules = this.generalRulesform.value
  }

  editBasicInfo(){
    const dialogRef = this.dialog.open(EditBasicCatalogsInfosComponent, {
      data: {
        image: this.imageCatalog,
        name: this.form.get('name')?.value,
        status: this.form.get('status')?.value,
        start_date: this.form.get('start_date')?.value,
        end_date: this.form.get('end_date')?.value,
        isEdit: false,
      },
      disableClose: true,
    })
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.imageCatalog = result.image;
        this.catalogImgFile = result.catalogImgFile;
        this.form.get('name')?.setValue(result.name);
        this.form.get('status')?.setValue(result.status);
        this.form.get('start_date')?.setValue(result.start_date);
        this.form.get('end_date')?.setValue(result.end_date);
      }
    });
  }

  editGeneralRules(){
    const dialogRef = this.dialog.open(EditGeneralRulesComponent, {
      data: {
        generalRules: this.generalRules,
        selectedProducts: this.selectedProducts,
        isEdit: false,
      },
      disableClose: true,
    })
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.generalRules = result.generalRules;
        this.selectedProducts = result.selectedProducts;
        this.initializeGeneralRulesForm(this.generalRules);
      }
    });
  }

  save(): boolean | void{
    if(!this.form.valid){
      this.form.markAllAsTouched();
      return false
    };

    // Check if any of the steps that need validation are invalid
    const isSomeStepInvalid = [1, 3].some((step: number) => !this.validateStep(step))

    if(isSomeStepInvalid) {
      return; // Exit if any step is invalid
    }

    const general_discount = this.generalRulesform.get('general_discount')?.value;
    const increment = this.generalRulesform.get('increment')?.value;
    const max_qte = this.generalRulesform.get('max_qte')?.value;
    const min_qte = this.generalRulesform.get('min_qte')?.value;
    const can_be_sold_out_of_stock = this.generalRulesform.get('can_be_sold_out_of_stock')?.value;
    const show_stock = this.generalRulesform.get('show_stock')?.value;
    const startDate = this.form.get('start_date')?.value;
    const endDate = this.form.get('end_date')?.value;

    this.selectedProducts = this.selectedProducts.map((product: IProduct, index) => {
      return {
        ...product,
        discount: product.discount ? product.discount : general_discount,
        increment: product.increment ? product.increment : increment,
        min_qte: product.min_qte ? product.min_qte : min_qte,
        max_qte: product.max_qte ? product.max_qte : max_qte,
        variants: product.variants ? product.variants.map(variant => {
                return {
                  ...variant,
            discount: variant.discount ??general_discount,
            increment: variant.increment ?? increment,
            min_qte: variant.min_qte ?? min_qte,
            max_qte: variant.max_qte ?? max_qte
          }
        }) : undefined
      }
    })
    const locations_ids: number[] = [];
    this.selectedLocations.forEach(location => {
      locations_ids.push(location.id!)
    })

    const payload: Omit<ICatalog, 'id'> = {
      name: this.form.get('name')?.value,
      image: this.imageCatalog ?? '',
      products: this.selectedProducts,
      locations_ids: locations_ids,
      start_date: startDate ? startDate : null,
      end_date: endDate ? endDate : null,
      status: this.form.get('status')?.value,
      general_discount: general_discount,
      increment: increment,
      max_qte: max_qte,
      min_qte: min_qte,
      can_be_sold_out_of_stock: can_be_sold_out_of_stock,
      show_stock: show_stock
    }



    this.store.dispatch(AddCatalog({catalog: payload}))
  }

  canceled(){
    this.router.navigate(['catalogs'], {
      queryParamsHandling: 'merge',
    })
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub => sub.unsubscribe()))
  }

  getLengthProductsPlusVariants() {
    let length = 0;
    this.selectedProducts.forEach(product => {
      length += product.variants?.length || 1;
    });
    return length;
  }
}
